import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Text,
  Stack,
  Group,
  Badge,
  Paper,
  SimpleGrid,
  Alert,
  Box,
  Skeleton,
  Button,
  Collapse,
  UnstyledButton,
  type MantineTheme
} from '@mantine/core';
import {
  IconChevronDown,
  IconChevronRight,
} from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '../common/PageHeader';
import { SectionHeader } from '../common/SectionHeader';
import {
  AdminComponentProjectMetrics,
  getMetricsFreshnessTimestamp,
  hasComponentProjectMetricsContent,
} from './admin/AdminComponentProjectMetrics';
import { formatTimestampToLocal } from '../../utils/dateUtils';
import { useAdminComponentProjectFilterStore } from '../../stores/adminComponentProjectFilterStore';
import { dashboardApi } from '../../api/dashboard';
import dayjs from 'dayjs';
import {
  submissionStateToDisplayStatus,
} from '../../types/status';
import {
  getAdminInitiativeLeads,
  getAllOutstandingItems,
  type AdminInitiativeLead,
  type OutstandingBucket,
  type OutstandingItemsSort,
  type OutstandingMilestone,
  type SubawardeeReportRow
} from '../../api/admin';
import { extractErrorMessage } from '../../utils/notifications';
import { withAdminSubawardeeQuery } from '../../utils/adminSubawardeePaths';
import {
  buildComponentProjectIdFilterParam,
  getComponentProjectDisplayName,
} from '../../config/componentProjects';
import { ADMIN_INITIATIVE_LEADS_QUERY_KEY } from '../../hooks/useAdminInitiativeLeadsQuery';
import { pluralize } from '../../utils/pluralize';
import { getInitiativeLeadDisplayName } from '../../utils/initiativeLeadUtils';
import { AllProjectsSpendOverview } from '../financials/AllProjectsSpendOverview';

/** Initial visible count / increment for each outstanding dashboard section. */
const OUTSTANDING_SECTION_PAGE_SIZE = 5;

const OUTSTANDING_SKELETON_CARD_COUNT = 3;

/** Default sort while outstanding section filters are hidden. */
const OUTSTANDING_DEFAULT_SORT: OutstandingItemsSort = 'dueDate-asc';

function OutstandingMilestonesCardSkeleton() {
  return (
    <Paper
      shadow="md"
      p="md"
      radius="md"
      aria-hidden
      sx={(theme) => ({
        border: theme.colorScheme === 'dark' ? `1px solid ${theme.colors.dark[4]}` : 'none',
        boxShadow:
          theme.colorScheme === 'dark'
            ? '0 0 12px rgba(255, 255, 255, 0.1)'
            : theme.shadows.md
      })}
    >
      <Stack spacing="sm">
        <Group position="apart" align="flex-start" noWrap>
          <Group spacing="xs" noWrap sx={{ flex: 1, minWidth: 0 }}>
            <Skeleton height={18} width={18} radius="sm" />
            <Stack spacing={4} sx={{ flex: 1, minWidth: 0 }}>
              <Skeleton height={22} width="38%" radius="sm" />
              <Group spacing={6}>
                <Skeleton height={18} width={72} radius="sm" />
                <Skeleton height={18} width={88} radius="sm" />
              </Group>
            </Stack>
          </Group>
          <Skeleton height={12} width={70} radius="sm" sx={{ flexShrink: 0 }} />
        </Group>
      </Stack>
    </Paper>
  );
}

function OutstandingReportsCardSkeleton() {
  return (
    <Paper
      shadow="md"
      p="md"
      radius="md"
      aria-hidden
      sx={(theme) => ({
        border: theme.colorScheme === 'dark' ? `1px solid ${theme.colors.dark[4]}` : 'none',
        boxShadow:
          theme.colorScheme === 'dark'
            ? '0 0 12px rgba(255, 255, 255, 0.1)'
            : theme.shadows.md
      })}
    >
      <Stack spacing="sm">
        <Group position="apart" align="flex-start" noWrap>
          <Group spacing="xs" noWrap sx={{ flex: 1, minWidth: 0 }}>
            <Skeleton height={18} width={18} radius="sm" />
            <Stack spacing={4} sx={{ flex: 1, minWidth: 0 }}>
              <Skeleton height={22} width="42%" radius="sm" />
              <Skeleton height={14} width="36%" radius="sm" />
            </Stack>
          </Group>
          <Stack spacing={4} align="flex-end" sx={{ flexShrink: 0 }}>
            <Skeleton height={12} width={90} radius="sm" />
            <Skeleton height={12} width={70} radius="sm" />
          </Stack>
        </Group>
      </Stack>
    </Paper>
  );
}

function mergeSubawardeeReports(bucket: OutstandingBucket): SubawardeeReportRow[] {
  const rows: SubawardeeReportRow[] = [];
  for (const block of bucket.reports) {
    rows.push(...block.subawardee_reports);
  }
  return rows;
}

function isActionableSubreport(row: SubawardeeReportRow): boolean {
  return row.submission_status.state.toLowerCase() !== 'approved';
}

function isNonCompletedMilestone(milestone: OutstandingMilestone): boolean {
  // API defines outstanding milestones; only guard against completed rows in the payload.
  return (milestone.status || 'not_started').toLowerCase() !== 'completed';
}

function bucketHasActionableReports(bucket: OutstandingBucket): boolean {
  return mergeSubawardeeReports(bucket).some(isActionableSubreport);
}

interface ComponentProjectMilestones {
  componentProjectId: string;
  milestones: OutstandingMilestone[];
}

function compareReportingPeriodDueDates(a: OutstandingBucket, b: OutstandingBucket): number {
  return (
    dayjs(a.reporting_period.due_date).valueOf() - dayjs(b.reporting_period.due_date).valueOf()
  );
}

function sortReportBuckets(
  buckets: OutstandingBucket[],
  sortBy: OutstandingItemsSort
): OutstandingBucket[] {
  const sorted = [...buckets];

  sorted.sort((a, b) => {
    switch (sortBy) {
      case 'dueDate-desc':
        return compareReportingPeriodDueDates(b, a);
      case 'component-asc': {
        const projectCompare = getComponentProjectDisplayName(a.component_project_id).localeCompare(
          getComponentProjectDisplayName(b.component_project_id)
        );
        return projectCompare !== 0 ? projectCompare : compareReportingPeriodDueDates(a, b);
      }
      case 'component-desc': {
        const projectCompare = getComponentProjectDisplayName(b.component_project_id).localeCompare(
          getComponentProjectDisplayName(a.component_project_id)
        );
        return projectCompare !== 0 ? projectCompare : compareReportingPeriodDueDates(a, b);
      }
      case 'dueDate-asc':
      default:
        return compareReportingPeriodDueDates(a, b);
    }
  });

  return sorted;
}

function milestoneDedupeKey(milestone: OutstandingMilestone): string {
  // Component-project trees reuse ids across subawardee copies — key by full path.
  return [
    milestone.id,
    milestone.objective_id ?? '',
    milestone.key_activity_id ?? '',
    milestone.goal_id ?? '',
    milestone.activity_id ?? '',
    milestone.subawardee ?? '',
  ].join(':');
}

function groupMilestonesByComponentProject(
  buckets: OutstandingBucket[],
  sortBy: OutstandingItemsSort
): ComponentProjectMilestones[] {
  const grouped = new Map<string, Map<string, OutstandingMilestone>>();

  for (const bucket of buckets) {
    for (const milestone of bucket.milestones) {
      if (!isNonCompletedMilestone(milestone)) continue;

      const projectMilestones =
        grouped.get(bucket.component_project_id) ?? new Map<string, OutstandingMilestone>();
      projectMilestones.set(milestoneDedupeKey(milestone), milestone);
      grouped.set(bucket.component_project_id, projectMilestones);
    }
  }

  const groups = Array.from(grouped.entries()).map(([componentProjectId, milestonesById]) => ({
    componentProjectId,
    milestones: Array.from(milestonesById.values()).sort(
      (a, b) => dayjs(a.due_date).valueOf() - dayjs(b.due_date).valueOf()
    )
  }));

  groups.sort((a, b) => {
    const nameA = getComponentProjectDisplayName(a.componentProjectId);
    const nameB = getComponentProjectDisplayName(b.componentProjectId);

    switch (sortBy) {
      case 'component-desc':
        return nameB.localeCompare(nameA);
      case 'dueDate-asc':
      case 'dueDate-desc': {
        const earliestA = a.milestones[0]?.due_date;
        const earliestB = b.milestones[0]?.due_date;
        if (!earliestA && !earliestB) return nameA.localeCompare(nameB);
        if (!earliestA) return 1;
        if (!earliestB) return -1;
        const dueDateCompare = dayjs(earliestA).valueOf() - dayjs(earliestB).valueOf();
        return sortBy === 'dueDate-desc' ? -dueDateCompare : dueDateCompare;
      }
      case 'component-asc':
      default:
        return nameA.localeCompare(nameB);
    }
  });

  return groups;
}

interface SectionVisibleState<T> {
  items: T[];
  hasMore: boolean;
}

function limitSectionItems<T>(items: T[], visibleCount: number): SectionVisibleState<T> {
  const totalItems = items.length;
  const cappedVisible = Math.min(Math.max(visibleCount, 0), totalItems);

  return {
    items: items.slice(0, cappedVisible),
    hasMore: cappedVisible < totalItems,
  };
}

const milestoneStatusConfig: Record<
  string,
  { label: string; color: string }
> = {
  not_started: { label: 'Not Started', color: 'dark' },
  in_progress: { label: 'In Progress', color: 'blue' },
  blocked: { label: 'Blocked', color: 'red' },
  completed: { label: 'Completed', color: 'green' }
};

function milestoneStatusTokenToLabel(status: string): string {
  const key = (status || 'not_started').toLowerCase();
  const entry = milestoneStatusConfig[key];
  if (entry) return entry.label;
  return key
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

const milestoneStatusSummaryOrder = ['blocked', 'in_progress', 'not_started'] as const;

function getMilestoneStatusSummary(milestones: OutstandingMilestone[]) {
  const counts = new Map<string, number>();

  for (const milestone of milestones) {
    const key = (milestone.status || 'not_started').toLowerCase();
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }

  return milestoneStatusSummaryOrder
    .filter((status) => (counts.get(status) ?? 0) > 0)
    .map((status) => ({
      status,
      count: counts.get(status) ?? 0,
      label: milestoneStatusConfig[status]?.label ?? milestoneStatusTokenToLabel(status),
      color: milestoneStatusConfig[status]?.color ?? 'gray'
    }));
}

function clickableItemBoxSx(theme: MantineTheme) {
  return {
    borderRadius: theme.radius.sm,
    border: `1px solid ${
      theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[3]
    }`,
    backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[7] : theme.white,
    padding: theme.spacing.sm,
    cursor: 'pointer',
    transition: 'background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease',
    '&:hover': {
      backgroundColor:
        theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[0],
      borderColor: theme.colorScheme === 'dark' ? theme.colors.dark[2] : theme.colors.gray[4]
    },
    '&:focus-visible': {
      outline: `2px solid ${
        theme.colorScheme === 'dark' ? theme.colors.gray[5] : theme.colors.gray[6]
      }`,
      outlineOffset: 1
    }
  };
}

function outstandingCardToggleButtonSx(theme: MantineTheme) {
  return {
    width: '100%',
    display: 'block',
    textAlign: 'left' as const,
    color: theme.colorScheme === 'dark' ? theme.colors.gray[0] : theme.colors.dark[9],
    borderRadius: theme.radius.sm,
    padding: theme.spacing.xs,
    transition: 'background-color 0.15s ease',
    '&:hover': {
      backgroundColor:
        theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[0]
    },
    '&:focus-visible': {
      outline: `2px solid ${
        theme.colorScheme === 'dark' ? theme.colors.gray[5] : theme.colors.gray[6]
      }`,
      outlineOffset: 2
    }
  };
}

interface OutstandingCardCollapseHeaderProps {
  expanded: boolean;
  panelId: string;
  onToggle: () => void;
  title: string;
  subtitle?: string;
  summary?: React.ReactNode;
  meta: React.ReactNode;
}

function OutstandingCardCollapseHeader({
  expanded,
  panelId,
  onToggle,
  title,
  subtitle,
  summary,
  meta
}: OutstandingCardCollapseHeaderProps) {
  return (
    <UnstyledButton
      onClick={onToggle}
      aria-expanded={expanded}
      aria-controls={panelId}
      sx={(theme) => outstandingCardToggleButtonSx(theme)}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 8,
          width: '100%'
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              minWidth: 0
            }}
          >
            <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
              {expanded ? <IconChevronDown size={18} /> : <IconChevronRight size={18} />}
            </Box>
            <Text weight={600} size="lg" lineClamp={1} sx={{ flex: 1, minWidth: 0 }}>
              {title}
            </Text>
          </Box>
          {subtitle && (
            <Text size="sm" color="dimmed" lineClamp={1} mt={4} pl={26}>
              {subtitle}
            </Text>
          )}
          {!expanded && summary && (
            <Box mt={4} pl={26}>
              {summary}
            </Box>
          )}
        </Box>

        <Box sx={{ flexShrink: 0, ml: 8 }}>{meta}</Box>
      </Box>
    </UnstyledButton>
  );
}

interface OutstandingMilestonesProjectCardProps {
  componentProjectId: string;
  projectDisplayName: string;
  milestones: OutstandingMilestone[];
  initiativeLeads: AdminInitiativeLead[];
  onViewMilestone: (milestone: OutstandingMilestone) => void;
}

function OutstandingMilestonesProjectCard({
  componentProjectId,
  projectDisplayName,
  milestones,
  initiativeLeads,
  onViewMilestone
}: OutstandingMilestonesProjectCardProps) {
  const [expanded, setExpanded] = useState(false);
  const statusSummary = useMemo(() => getMilestoneStatusSummary(milestones), [milestones]);
  const panelId = `milestones-panel-${componentProjectId}`;

  return (
    <Paper
      shadow="md"
      p="md"
      radius="md"
      role="article"
      aria-label={`${projectDisplayName} outstanding milestones`}
      sx={(theme) => ({
        border: theme.colorScheme === 'dark' ? `1px solid ${theme.colors.dark[4]}` : 'none',
        boxShadow:
          theme.colorScheme === 'dark'
            ? '0 0 12px rgba(255, 255, 255, 0.1)'
            : theme.shadows.md
      })}
    >
      <Stack spacing="sm">
        <OutstandingCardCollapseHeader
          expanded={expanded}
          panelId={panelId}
          onToggle={() => setExpanded((current) => !current)}
          title={projectDisplayName}
          summary={
            <Group spacing={6}>
              {statusSummary.map((entry) => (
                <Badge key={entry.status} color={entry.color} variant="light" size="xs">
                  {entry.count} {entry.label}
                </Badge>
              ))}
            </Group>
          }
          meta={
            <Text size="xs" color="dimmed" weight={500}>
              {milestones.length} outstanding
            </Text>
          }
        />

        <Collapse in={expanded}>
          <Stack id={panelId} spacing="xs" mt="xs">
            {milestones.map((milestone) => {
              const statusKey = (milestone.status || 'not_started').toLowerCase();
              const config = milestoneStatusConfig[statusKey] ?? milestoneStatusConfig.not_started;

              return (
                <Box
                  key={milestoneDedupeKey(milestone)}
                  role="button"
                  tabIndex={0}
                  aria-label={`Open ${milestone.title} milestone details`}
                  onClick={() => onViewMilestone(milestone)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      onViewMilestone(milestone);
                    }
                  }}
                  sx={(theme) => clickableItemBoxSx(theme)}
                >
                  <Stack spacing="sm">
                    <Group position="apart" align="flex-start" noWrap>
                      <Stack spacing={2} sx={{ flex: 1, minWidth: 0 }}>
                        <Text size="sm" weight={600} lineClamp={1}>
                          {milestone.title}
                        </Text>
                        <Text size="xs" color="dimmed" weight={500} lineClamp={1}>
                          {milestone.subawardee || projectDisplayName}
                        </Text>
                      </Stack>
                      <Badge color={config.color} variant="light" size="sm" sx={{ flexShrink: 0 }}>
                        {config.label}
                      </Badge>
                    </Group>
                    <Group position="apart" align="center" noWrap>
                      <Text size="xs" color="dimmed" weight={500}>
                        {getInitiativeLeadDisplayName(milestone.initiative_lead, initiativeLeads) ??
                          ''}
                      </Text>
                      <Text size="xs" color="dimmed" weight={500}>
                        Due: {dayjs(milestone.due_date).format('MMM D, YYYY')}
                      </Text>
                    </Group>
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        </Collapse>
      </Stack>
    </Paper>
  );
}

interface OutstandingReportsPeriodCardProps {
  bucket: OutstandingBucket;
  projectDisplayName: string;
  initiativeLeads: AdminInitiativeLead[];
  onViewReport: (reportId: string, subawardee: string) => void;
}

function OutstandingReportsPeriodCard({
  bucket,
  projectDisplayName,
  initiativeLeads,
  onViewReport,
}: OutstandingReportsPeriodCardProps) {
  const [expanded, setExpanded] = useState(false);
  const reportingPeriod = bucket.reporting_period;
  const mergedSubs = mergeSubawardeeReports(bucket);
  const actionableSubawardees = mergedSubs.filter(isActionableSubreport);
  const periodLabel =
    bucket.reports[0]?.reporting_period_label ?? reportingPeriod.reporting_period_label;
  const panelId = `reports-panel-${bucket.component_project_id}-${reportingPeriod.reporting_year}-${reportingPeriod.reporting_period_type}-${reportingPeriod.reporting_period_value}`;

  const urgencyInfo = useMemo(() => {
    const daysUntilDue = dayjs(reportingPeriod.due_date)
      .startOf('day')
      .diff(dayjs().startOf('day'), 'day');

    if (daysUntilDue >= 0 && daysUntilDue <= 7) {
      return {
        label: `Due in ${daysUntilDue} days`,
        color: 'blue',
        variant: 'light' as const
      };
    }
    return null;
  }, [reportingPeriod.due_date]);

  return (
    <Paper
      shadow="md"
      p="md"
      radius="md"
      role="article"
      aria-label={`${projectDisplayName} ${periodLabel}`}
      sx={(theme) => ({
        border: theme.colorScheme === 'dark' ? `1px solid ${theme.colors.dark[4]}` : 'none',
        boxShadow:
          theme.colorScheme === 'dark'
            ? '0 0 12px rgba(255, 255, 255, 0.1)'
            : theme.shadows.md
      })}
    >
      <Stack spacing="sm">
        <OutstandingCardCollapseHeader
          expanded={expanded}
          panelId={panelId}
          onToggle={() => setExpanded((current) => !current)}
          title={projectDisplayName}
          subtitle={periodLabel}
          meta={
            <Stack spacing={4} align="flex-end">
              <Text size="xs" color="dimmed" weight={500}>
                Due: {dayjs(reportingPeriod.due_date).format('MMM D, YYYY')}
              </Text>
              {urgencyInfo && (
                <Badge color={urgencyInfo.color} variant={urgencyInfo.variant} size="xs">
                  {urgencyInfo.label}
                </Badge>
              )}
              <Text size="xs" color="dimmed" weight={500}>
                {actionableSubawardees.length}{' '}
                {pluralize(actionableSubawardees.length, 'needs', 'need')} attention
              </Text>
            </Stack>
          }
        />

        <Collapse in={expanded}>
          <Stack id={panelId} spacing="xs" mt="xs">
            {actionableSubawardees.map((subreport) => {
              const displayStatus = submissionStateToDisplayStatus(subreport.submission_status.state);
              return (
                <Box
                  key={`${subreport.report_id}-${subreport.subawardee}`}
                  role="button"
                  tabIndex={0}
                  aria-label={`Open ${subreport.subawardee} report`}
                  onClick={() => onViewReport(subreport.report_id, subreport.subawardee)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      onViewReport(subreport.report_id, subreport.subawardee);
                    }
                  }}
                  sx={(theme) => clickableItemBoxSx(theme)}
                >
                  <Stack spacing="sm">
                    <Group position="apart" align="flex-start" noWrap>
                      <Text size="sm" weight={600} lineClamp={1} sx={{ flex: 1, minWidth: 0 }}>
                        {subreport.subawardee}
                      </Text>
                      {displayStatus === 'pending' &&
                        subreport.days_until_auto_approval != null &&
                        subreport.days_until_auto_approval <= 7 && (
                          <Text size={11} color="dimmed" weight={500} sx={{ flexShrink: 0 }}>
                            Auto-approves in {subreport.days_until_auto_approval}d
                          </Text>
                        )}
                    </Group>
                    <Box sx={{ minHeight: 22, display: 'flex', alignItems: 'center' }}>
                      <Text size="xs" color="dimmed" weight={500}>
                        {getInitiativeLeadDisplayName(subreport.initiative_lead, initiativeLeads) ??
                          'Unknown'}
                      </Text>
                    </Box>
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        </Collapse>
      </Stack>
    </Paper>
  );
}

export function AdminDashboard() {
  const navigate = useNavigate();
  const componentProject = useAdminComponentProjectFilterStore((s) => s.componentProject);
  const [milestonesVisibleCount, setMilestonesVisibleCount] = useState(OUTSTANDING_SECTION_PAGE_SIZE);
  const [reportsVisibleCount, setReportsVisibleCount] = useState(OUTSTANDING_SECTION_PAGE_SIZE);

  useEffect(() => {
    setMilestonesVisibleCount(OUTSTANDING_SECTION_PAGE_SIZE);
    setReportsVisibleCount(OUTSTANDING_SECTION_PAGE_SIZE);
  }, [componentProject]);

  const componentProjectMetricsQuery = useQuery({
    queryKey: ['admin-component-project-metrics', componentProject, 'with-goals'],
    queryFn: () =>
      dashboardApi.getComponentProjectMetrics(componentProject!, { includeGoals: true }),
    enabled: componentProject != null,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const { data: initiativeLeads = [] } = useQuery({
    queryKey: ADMIN_INITIATIVE_LEADS_QUERY_KEY,
    queryFn: getAdminInitiativeLeads,
    staleTime: 10 * 60 * 1000
  });

  const handleViewReport = (reportId: string, subawardee: string) => {
    navigate(withAdminSubawardeeQuery(`/admin/reports/${reportId}`, subawardee));
  };

  const componentProjectIdQueryParam = useMemo(
    () => buildComponentProjectIdFilterParam(componentProject),
    [componentProject]
  );

  const handleViewMilestones = (milestone: OutstandingMilestone) => {
    const projectId =
      buildComponentProjectIdFilterParam(milestone.component_project_id) ??
      componentProjectIdQueryParam;
    // Component-project milestones live on the CP tree, not a subawardee doc.
    if (projectId && (!milestone.subawardee || !milestone.report_id)) {
      navigate(
        `/admin/milestones?view=component&component_project=${encodeURIComponent(projectId)}`
      );
      return;
    }
    if (milestone.subawardee) {
      navigate(
        `/admin/milestones?view=subawardee&subawardee=${encodeURIComponent(milestone.subawardee)}`
      );
      return;
    }
    navigate('/admin/milestones?view=component');
  };

  // Outstanding section filters (lead / sort / status) are temporarily hidden.
  // Re-add per-section toolbars + independent queries when bringing filters back.
  const {
    data: buckets = [],
    isLoading: isOutstandingLoading,
    isFetching: isOutstandingFetching,
    error: outstandingError,
    isError: isOutstandingError,
  } = useQuery({
    queryKey: ['admin-outstanding-items', OUTSTANDING_DEFAULT_SORT, componentProject],
    queryFn: () =>
      getAllOutstandingItems({
        sort_by: OUTSTANDING_DEFAULT_SORT,
        ...(componentProjectIdQueryParam && {
          component_project_id: componentProjectIdQueryParam,
        }),
      }),
    staleTime: 5 * 60 * 1000,
  });

  const allMilestoneGroups = useMemo(
    () => groupMilestonesByComponentProject(buckets, OUTSTANDING_DEFAULT_SORT),
    [buckets]
  );

  const allReportBuckets = useMemo(
    () =>
      sortReportBuckets(
        buckets.filter(bucketHasActionableReports),
        OUTSTANDING_DEFAULT_SORT
      ),
    [buckets]
  );

  const milestonesVisible = useMemo(
    () => limitSectionItems(allMilestoneGroups, milestonesVisibleCount),
    [allMilestoneGroups, milestonesVisibleCount]
  );

  const reportsVisible = useMemo(
    () => limitSectionItems(allReportBuckets, reportsVisibleCount),
    [allReportBuckets, reportsVisibleCount]
  );

  const hasOutstandingMilestones = allMilestoneGroups.length > 0;
  const hasOutstandingReports = allReportBuckets.length > 0;

  // Hide empty sections once loaded (same pattern as sub-awardee dashboard).
  const showOutstandingMilestonesSection =
    isOutstandingError || isOutstandingLoading || hasOutstandingMilestones;
  const showOutstandingReportsSection =
    isOutstandingError || isOutstandingLoading || hasOutstandingReports;
  const showOutstandingSection =
    isOutstandingError ||
    isOutstandingLoading ||
    hasOutstandingMilestones ||
    hasOutstandingReports;

  const showMetricsSection = useMemo(() => {
    if (!componentProject) return false;
    const { isLoading, isFetching, isError, data } = componentProjectMetricsQuery;
    if (isLoading || isFetching) return true;
    if (isError) return true;
    return hasComponentProjectMetricsContent(componentProject, data);
  }, [componentProject, componentProjectMetricsQuery]);

  const metricsFreshnessTooltip = useMemo(() => {
    const timestamp = getMetricsFreshnessTimestamp(componentProjectMetricsQuery.data);
    return timestamp ? `Last updated ${formatTimestampToLocal(timestamp)}` : undefined;
  }, [componentProjectMetricsQuery.data]);

  return (
    <Container size="xl" mt="xl" pb={80} sx={{ minHeight: 'calc(100vh - 140px)' }}>
      <PageHeader
        title="Admin Dashboard"
        subtitle="Monitor component project health, track quarterly report submissions, and manage milestones across all sub-awardees"
      />

      <Box mb={60}>
        <SectionHeader title="Financials" />
        <AllProjectsSpendOverview
          componentProjectId={componentProject}
          showTitle={false}
        />
      </Box>

      {showMetricsSection ? (
        <Box mb={60}>
          <SectionHeader title="Metrics" infoTooltip={metricsFreshnessTooltip} />
          <AdminComponentProjectMetrics
            projectId={componentProject!}
            metricsQuery={componentProjectMetricsQuery}
          />
        </Box>
      ) : null}

      {showOutstandingSection ? (
        <Box mb={60} sx={{ scrollMarginTop: 24 }}>
          {isOutstandingError && (
            <Alert title="Could not load outstanding items" color="red" mb="md">
              {extractErrorMessage(
                outstandingError,
                'Unable to fetch admin dashboard data. Please try again.'
              )}
            </Alert>
          )}

          {isOutstandingLoading ? (
            <Box role="status" aria-busy="true" aria-label="Loading outstanding items" mb="md">
              <Box mb={48}>
                <SectionHeader title="Outstanding Milestones" />
                <Stack spacing="lg">
                  {Array.from({ length: OUTSTANDING_SKELETON_CARD_COUNT }, (_, i) => (
                    <OutstandingMilestonesCardSkeleton key={`milestone-skeleton-${i}`} />
                  ))}
                </Stack>
              </Box>
              <Box>
                <SectionHeader title="Outstanding Reports" />
                <Stack spacing="lg">
                  {Array.from({ length: OUTSTANDING_SKELETON_CARD_COUNT }, (_, i) => (
                    <OutstandingReportsCardSkeleton key={`report-skeleton-${i}`} />
                  ))}
                </Stack>
              </Box>
            </Box>
          ) : null}

          {!isOutstandingLoading && !isOutstandingError ? (
            <>
              {showOutstandingMilestonesSection ? (
                <Box
                  mb={showOutstandingReportsSection ? 48 : 0}
                  sx={{ scrollMarginTop: 24 }}
                >
                  <SectionHeader title="Outstanding Milestones" />
                  <SimpleGrid cols={1} spacing="lg">
                    {milestonesVisible.items.map((group) => (
                      <OutstandingMilestonesProjectCard
                        key={group.componentProjectId}
                        componentProjectId={group.componentProjectId}
                        projectDisplayName={getComponentProjectDisplayName(
                          group.componentProjectId
                        )}
                        milestones={group.milestones}
                        initiativeLeads={initiativeLeads}
                        onViewMilestone={handleViewMilestones}
                      />
                    ))}
                  </SimpleGrid>
                  {milestonesVisible.hasMore ? (
                    <Group position="center" mt="xl" pt="md">
                      <Button
                        variant="subtle"
                        color="dark"
                        disabled={isOutstandingFetching}
                        onClick={() =>
                          setMilestonesVisibleCount(
                            (count) => count + OUTSTANDING_SECTION_PAGE_SIZE
                          )
                        }
                      >
                        Show more
                      </Button>
                    </Group>
                  ) : null}
                </Box>
              ) : null}

              {showOutstandingReportsSection ? (
                <Box sx={{ scrollMarginTop: 24 }}>
                  <SectionHeader title="Outstanding Reports" />
                  <SimpleGrid cols={1} spacing="lg">
                    {reportsVisible.items.map((bucket) => (
                      <OutstandingReportsPeriodCard
                        key={`${bucket.component_project_id}-${bucket.reporting_period.reporting_year}-${bucket.reporting_period.reporting_period_type}-${bucket.reporting_period.reporting_period_value}`}
                        bucket={bucket}
                        projectDisplayName={getComponentProjectDisplayName(
                          bucket.component_project_id
                        )}
                        initiativeLeads={initiativeLeads}
                        onViewReport={handleViewReport}
                      />
                    ))}
                  </SimpleGrid>
                  {reportsVisible.hasMore ? (
                    <Group position="center" mt="xl" pt="md">
                      <Button
                        variant="subtle"
                        color="dark"
                        disabled={isOutstandingFetching}
                        onClick={() =>
                          setReportsVisibleCount((count) => count + OUTSTANDING_SECTION_PAGE_SIZE)
                        }
                      >
                        Show more
                      </Button>
                    </Group>
                  ) : null}
                </Box>
              ) : null}
            </>
          ) : null}
        </Box>
      ) : null}
    </Container>
  );
}
